import './capture-waypoint-button.css';

import { IonLabel, IonSpinner } from '@ionic/react';
import { throttle } from 'lodash';
import {
  MutableRefObject,
  ReactElement,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useStopProgram } from '../../../../../api/hooks/binding/script-action.binding';
import { BaseButton } from '../../../../../components/button/base-button';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { QNear, Waypoint } from '../../../../../util/machine-variable.utils';
import { RobotTeachError } from '../../hook/flow-teach.hooks';
import {
  WaypointCaptureRef,
  useUpdateFlowItemFormOnWaypointCapture,
  useWaypointCapture,
} from '../../hook/waypoint-capture.hooks';

export interface CaptureWaypointButtonRef {
  click: () => void;
}

interface CaptureWaypointButtonProps {
  cancelButtonText?: string;
  captureButtonText?: string;
  disabled?: boolean;
  stopProgramFirst?: boolean;
  onCapture: (waypoint: Waypoint, qnear: QNear) => void;
  onCaptureStart?: () => void;
  onCaptureCancel?: () => void;
  onLongPress?: () => void;
  innerRef?: MutableRefObject<WaypointCaptureRef | undefined>;
}

const CaptureWaypointButton = forwardRef<
  CaptureWaypointButtonRef,
  CaptureWaypointButtonProps
>((props, ref): ReactElement => {
  const {
    cancelButtonText,
    captureButtonText,
    disabled,
    stopProgramFirst,
    onCapture,
    onCaptureStart,
    onCaptureCancel,
    onLongPress,
    innerRef,
  } = props;
  const [isAwaitingCapture, setWaitForCapture] = useState<boolean>(false);

  const stopProgram = useStopProgram();

  const toggleCapture = useCallback(
    (nextState: boolean) => {
      if (nextState) {
        if (typeof onCaptureStart === 'function') {
          onCaptureStart();
        }

        if (stopProgramFirst) {
          stopProgram();
        }
      } else {
        if (typeof onCaptureCancel === 'function') {
          onCaptureCancel();
        }
      }

      setWaitForCapture(nextState);
    },
    [onCaptureStart, stopProgramFirst, stopProgram, onCaptureCancel]
  );

  const toggleCaptureThrottled = useMemo(() => {
    return throttle(toggleCapture, 300, { trailing: false });
  }, [toggleCapture]);

  // Expose click method to parent components
  useImperativeHandle(
    ref,
    () => ({
      click: () => {
        toggleCaptureThrottled(!isAwaitingCapture);
      },
    }),
    [toggleCaptureThrottled, isAwaitingCapture]
  );

  const buttonText = isAwaitingCapture
    ? cancelButtonText ?? getLocalizedText('common:touch_up_cancel')
    : captureButtonText ?? getLocalizedText('common:touch_up');

  return (
    <BaseButton
      buttonProps={{
        fill: isAwaitingCapture ? 'solid' : 'outline',
        onClick: () => {
          toggleCaptureThrottled(!isAwaitingCapture);
        },
        disabled,
      }}
    >
      <IonLabel>{buttonText}</IonLabel>
      {isAwaitingCapture && (
        <CaptureWaypointSpinner
          innerRef={innerRef}
          onCapture={(waypoint: Waypoint, qnear: QNear) => {
            setWaitForCapture(false);
            if (typeof onCapture === 'function') {
              onCapture(waypoint, qnear);
            }
          }}
          onError={() => {
            setWaitForCapture(false);
          }}
          onLongPress={onLongPress}
        />
      )}
    </BaseButton>
  );
});

CaptureWaypointButton.displayName = 'CaptureWaypointButton';

interface CaptureWaypointSpinnerProps {
  className?: string;
  onCapture: (waypoint: Waypoint, qnear: QNear) => void;
  onError?: (error: RobotTeachError) => void;
  onLongPress?: () => void;
  innerRef?: MutableRefObject<WaypointCaptureRef | undefined>;
}

const CaptureWaypointSpinner = (props: CaptureWaypointSpinnerProps) => {
  useWaypointCapture({
    innerRef: props.innerRef,
    onCapture: props.onCapture,
    onTeachError: props.onError,
    onLongPress: props.onLongPress,
  });

  return (
    <div className={props.className ?? 'chip-spinner'}>
      <IonSpinner />
    </div>
  );
};

interface CaptureWaypointButtonFlowItemFormProps
  extends CaptureWaypointButtonProps {
  autoSubmitForm?: boolean;
}

const CaptureWaypointButtonFlowItemForm = (
  props: Omit<CaptureWaypointButtonFlowItemFormProps, 'onCapture'>
) => {
  const { autoSubmitForm, ...otherProps } = props;
  const onCapture = useUpdateFlowItemFormOnWaypointCapture({ autoSubmitForm });

  return <CaptureWaypointButton onCapture={onCapture} {...otherProps} />;
};

export {
  CaptureWaypointButton,
  CaptureWaypointButtonFlowItemForm,
  CaptureWaypointSpinner,
};
